import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from './RootStore';
import { tokenManager } from '../api/tokenManager';

const BASE_URL = process.env.REACT_APP_API_URL?.replace('/graphql', '') || "http://localhost:4000";

export interface SSEMessage {
  type: string;
  payload: any;
  clientId?: string;
}

export interface SSEEventHandler {
  (payload: any, rootStore: RootStore): void;
}

type SSEConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

export class EventSourceStore {
  private eventSource: EventSource | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private tokenRefreshTimeout: NodeJS.Timeout | null = null;
  private readonly MAX_RECONNECT_DELAY = 30000;
  private readonly INITIAL_RECONNECT_DELAY = 1000;
  private reconnectDelay: number = this.INITIAL_RECONNECT_DELAY;
  private eventHandlers = new Map<string, SSEEventHandler>();
  private reconnectHandlers = new Set<(rootStore: RootStore) => void>();
  private hadConnection = false;

  reconnectAttempts: number = 0;
  connectionState: SSEConnectionState = 'disconnected';
  lastError: string | null = null;
  clientId: string | null = null;

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
  }

  /**
   * Registra un handler para un tipo de evento SSE específico.
   * Permite a cada proyecto registrar sus propios handlers sin modificar el generador.
   */
  registerHandler(eventType: string, handler: SSEEventHandler): void {
    this.eventHandlers.set(eventType, handler);
  }

  /**
   * Elimina un handler registrado.
   */
  unregisterHandler(eventType: string): void {
    this.eventHandlers.delete(eventType);
  }

  /**
   * Registra un handler que corre cada vez que la conexión SSE se RESTABLECE
   * (no en la conexión inicial). Los eventos emitidos durante la desconexión
   * se pierden (SSE no tiene replay): estos handlers son el punto de
   * resincronización — cada vista refresca lo suyo. Devuelve la función para
   * des-registrar.
   */
  registerReconnectHandler(handler: (rootStore: RootStore) => void): () => void {
    this.reconnectHandlers.add(handler);
    return () => this.reconnectHandlers.delete(handler);
  }

  async connect(): Promise<void> {
    if (this.connectionState === 'connecting' || this.connectionState === 'connected') {
      return;
    }

    const token = await tokenManager.getValidAccessToken();

    if (!token) {
      // Sin token válido (p.ej. refresh fallido porque el server está caído o
      // reiniciando): REINTENTAR con backoff, no abandonar. Un return sin
      // reintento mataba la conexión SSE para siempre en esta pestaña — la UI
      // dejaba de recibir eventos (firmas, workflows) hasta un reload manual.
      console.warn('[EventSource] No valid token available, retrying later');
      runInAction(() => {
        this.connectionState = 'error';
        this.lastError = 'No valid token available';
      });
      this.scheduleReconnect();
      return;
    }

    runInAction(() => {
      this.connectionState = 'connecting';
      this.lastError = null;
    });

    const url = `${BASE_URL}/api/events?token=${encodeURIComponent(token)}`;
    this.eventSource = new EventSource(url);

    this.eventSource.onopen = () => {
      runInAction(() => {
        this.connectionState = 'connected';
        this.reconnectAttempts = 0;
        this.reconnectDelay = this.INITIAL_RECONNECT_DELAY;
      });
      this.scheduleTokenRefresh();
    };

    this.eventSource.onerror = () => {
      this.handleConnectionError('Connection error');
    };

    this.eventSource.addEventListener('message', this.handleMessage);
  }

  private handleMessage = (event: MessageEvent): void => {
    try {
      const message: SSEMessage = JSON.parse(event.data);

      // Evento de conexión establecida
      if (message.type === 'connected' && message.clientId) {
        runInAction(() => {
          this.clientId = message.clientId ?? null;
          this.connectionState = 'connected';
          this.reconnectAttempts = 0;
          this.reconnectDelay = this.INITIAL_RECONNECT_DELAY;
        });
        // Reconexión (no primera conexión): pudimos habernos perdido eventos —
        // avisar a los handlers de resincronización.
        if (this.hadConnection) {
          for (const handler of this.reconnectHandlers) {
            try {
              handler(this.rootStore);
            } catch (error) {
              console.error('[EventSource] Reconnect handler error:', error);
            }
          }
        }
        this.hadConnection = true;
        return;
      }

      // Evento de estado del sistema (genérico del framework)
      if (message.type === 'system.status') {
        this.rootStore.systemStatusStore.handleSSEUpdate(message.payload);
        return;
      }

      // Reintento automático: señal CALMA (el proceso falló este intento pero el
      // sistema lo reintenta solo). NO es un error — no alarmar al usuario.
      if (message.type === 'pipeline.stage.retrying') {
        this.handlePipelineStageRetrying(message.payload);
        return;
      }

      // Fallo DEFINITIVO de pipeline (el worker lo publica solo al dead-letter).
      if (message.type === 'pipeline.stage.failed') {
        this.handlePipelineStageFailed(message.payload);
        // No retornamos - permite que handlers custom también procesen
      }

      // Avance de workflow (genérico): refrescar el View/Update de la entidad
      // para que el stepper del workflow se actualice en vivo (sin refresh).
      if (message.type === 'workflow.stateChanged') {
        this.handleWorkflowStateChanged(message.payload);
        return;
      }

      // Buscar handler registrado por el proyecto
      const handler = this.eventHandlers.get(message.type);
      if (handler) {
        handler(message.payload, this.rootStore);
      }

    } catch (error) {
      console.error('[EventSource] Message parse error:', error);
    }
  };

  /**
   * Handler genérico para errores de pipeline.
   * Muestra notificación, snackbar, y recarga el store afectado automáticamente.
   */
  private handlePipelineStageFailed(payload: {
    workerEventId: string;
    pipelineId: string;
    stageId: string;
    final?: boolean;
    error: {
      message: string;
      code?: string;
      validationErrors?: Array<{ field: string; message: string }>;
      suggestedAction?: string;
      detailType?: string;
      // Items estructurados localizables: el server no conoce el idioma del
      // usuario al lanzar, así que envía las variantes y el cliente elige.
      items?: Array<{
        id: string;
        label: string | { es: string; en: string; pt?: string };
      }>;
    };
    affectedEntity?: {
      entityType: string;
      entityId: string;
    };
  }): void {
    const { pipelineId, stageId, error, affectedEntity } = payload;
    const ls = this.rootStore.langStore;

    const title = ls.pipelineErrorTitle || 'No se pudo completar el proceso';
    // Mensaje base: preferir el texto localizado del código de error.
    const base = error.code && error.code in ls._apiServerErrors
      ? ls.apiServerError(error.code)
      : error.message;

    // Detalle accionable en una LISTA legible (no entre paréntesis): items
    // estructurados (p.ej. editoriales sin firmante) o errores de validación.
    const lang = this.rootStore.langStore.language as 'es' | 'en' | 'pt';
    const lines: string[] = [];
    if (error.items && error.items.length > 0) {
      lines.push(...error.items.map(it => {
        const label = typeof it.label === 'string'
          ? it.label
          : (it.label[lang] ?? it.label.es ?? it.id);
        return `• ${label}`;
      }));
    } else if (error.validationErrors && error.validationErrors.length > 0) {
      lines.push(...error.validationErrors.map(ve => `• ${ve.field}: ${ve.message}`));
    }

    let message = base;
    if (lines.length > 0) message = `${base}\n${lines.join('\n')}`;
    if (error.suggestedAction) message = `${message}\n${error.suggestedAction}`;

    this.rootStore.notificationStore.addNotification({
      type: 'error',
      title,
      message,
      data: { pipelineId, stageId, error },
    });

    this.rootStore.showSnackbar(message, 'error');

    // Recargar store afectado automáticamente si se especifica la entidad
    if (affectedEntity) {
      this.reloadAffectedStore(affectedEntity.entityType, affectedEntity.entityId);
    }
  }

  /**
   * Handler de REINTENTO automático. Señal CALMA: hubo un problema pero el
   * sistema lo reintenta solo — se informa sin alarmar (snackbar 'info' +
   * notificación 'info'), con el número de intento para dar contexto.
   */
  private handlePipelineStageRetrying(payload: {
    workerEventId: string;
    pipelineId: string;
    stageId: string;
    attempt: number;
    maxAttempts: number;
    nextRetryInSeconds?: number;
    affectedEntity?: { entityType: string; entityId: string };
  }): void {
    const { pipelineId, stageId, attempt, maxAttempts, affectedEntity } = payload;
    const ls = this.rootStore.langStore;

    const base = ls.pipelineRetryingMessage || 'Se produjo un problema; reintentando automáticamente';
    const message = `${base} (intento ${attempt} de ${maxAttempts})`;

    this.rootStore.notificationStore.addNotification({
      type: 'info',
      title: ls.pipelineRetryingTitle || 'Reintentando',
      message,
      data: { pipelineId, stageId, attempt, maxAttempts },
    });
    this.rootStore.showSnackbar(message, 'info');

    // Mantener el store afectado en sincronía (sigue en estado transitorio).
    if (affectedEntity) {
      this.reloadAffectedStore(affectedEntity.entityType, affectedEntity.entityId);
    }
  }

  /**
   * Handler genérico de avance de workflow. Refresca el store del View o
   * Update abierto de la entidad para que el stepper del workflow se
   * actualice en vivo (MobX re-renderiza solo al recargar la entidad).
   * Funciona para CUALQUIER entidad con workflow, sin código por-entidad.
   */
  private handleWorkflowStateChanged(payload: {
    entityName?: string;
    instanceId?: string;
    workflowName?: string;
    toState?: string;
  }): void {
    const raw = payload?.entityName || '';
    const entity = raw.charAt(0).toLowerCase() + raw.slice(1);
    const id = payload?.instanceId;
    if (!entity || !id) {
      return;
    }
    const cap = entity.charAt(0).toUpperCase() + entity.slice(1);
    const methodName = `load${cap}ById`;
    // El stepper puede estar abierto en el View o en el Update de la entidad.
    for (const kind of ['view', 'update']) {
      const store = this.rootStore.storeRegistry.getStoreIfExists(
        `/${entity}/${kind}/${id}`
      );
      if (!store) {
        continue;
      }
      if (typeof (store as any)[methodName] === 'function') {
        (store as any)[methodName](id);
      } else if (typeof (store as any).reload === 'function') {
        (store as any).reload();
      }
    }
  }

  /**
   * Intenta recargar el store de una entidad afectada.
   * Usa convenciones de nombres para encontrar el store y método de recarga.
   */
  private reloadAffectedStore(entityType: string, entityId: string): void {
    // Convención: storePath = /{entityType}/update/{entityId}
    const storePath = `/${entityType}/update/${entityId}`;
    const store = this.rootStore.storeRegistry.getStoreIfExists(storePath);

    if (!store) {
      return;
    }

    // Convención: método load{EntityType}ById (ej: loadEditionById)
    const capitalizedType = entityType.charAt(0).toUpperCase() + entityType.slice(1);
    const methodName = `load${capitalizedType}ById`;

    if (typeof (store as any)[methodName] === 'function') {
      (store as any)[methodName](entityId);
    } else if (typeof (store as any).reload === 'function') {
      // Fallback: método reload() genérico
      (store as any).reload();
    }
  }

  private handleConnectionError(errorMessage: string): void {
    runInAction(() => {
      this.connectionState = 'error';
      this.lastError = errorMessage;
    });

    this.disconnect();
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.MAX_RECONNECT_DELAY
    );

    this.reconnectTimeout = setTimeout(async () => {
      await this.connect();
    }, delay);
  }

  private scheduleTokenRefresh(): void {
    if (this.tokenRefreshTimeout) {
      clearTimeout(this.tokenRefreshTimeout);
      this.tokenRefreshTimeout = null;
    }

    const authState = tokenManager.getAuthState();
    if (!authState.accessExpiresAt) return;

    const now = Date.now();
    const timeUntilExpiry = authState.accessExpiresAt - now;
    const refreshIn = Math.max(timeUntilExpiry - 60000, 5000);

    this.tokenRefreshTimeout = setTimeout(() => {
      this.reconnectWithNewToken();
    }, refreshIn);
  }

  private async reconnectWithNewToken(): Promise<void> {
    this.disconnect();
    await this.connect();
  }

  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.tokenRefreshTimeout) {
      clearTimeout(this.tokenRefreshTimeout);
      this.tokenRefreshTimeout = null;
    }

    if (this.eventSource) {
      this.eventSource.removeEventListener('message', this.handleMessage);
      this.eventSource.close();
      this.eventSource = null;
    }

    runInAction(() => {
      this.connectionState = 'disconnected';
      this.clientId = null;
    });
  }

  get isConnected(): boolean {
    return this.connectionState === 'connected';
  }

  get isConnecting(): boolean {
    return this.connectionState === 'connecting';
  }
}
